import React from 'react';
import { Chip, type ChipProps } from '@mui/material';

import { colors } from '../../theme/tokens';

export type StatusKey = keyof typeof colors.status;

interface StatusBadgeProps extends Omit<ChipProps, 'color'> {
  status: StatusKey;
  customLabel?: string;
}

const STATUS_LABELS: Record<StatusKey, string> = {
  active:      'Active',
  injured:     'Injured',
  inactive:    'Inactive',
  inUse:       'In Use',
  available:   'Available',
  maintenance: 'Maintenance',
  inShop:      'In Shop',
  overdue:     'Overdue',
  due5days:    'Due in 5 days',
  due7days:    'Due in 7 days',
  race:        'Race',
  practice:    'Practice',
  test:        'Test',
  qualifying:  'Qualifying',
  positive:    'Positive',
  neutral:     'Neutral',
  needsWork:   'Needs Work',
};

export function StatusBadge({ status, customLabel, sx, size = 'small', ...rest }: StatusBadgeProps) {
  const cfg = colors.status[status];
  return (
    <Chip
      label={customLabel ?? STATUS_LABELS[status]}
      size={size}
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.text,
        border: `1px solid ${cfg.border}`,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
        borderRadius: '6px',
        '& .MuiChip-label': { px: 1 },
        ...sx,
      }}
      {...rest}
    />
  );
}

interface LapTimeBadgeProps {
  ms: number;
  isPersonalBest?: boolean;
  isSessionBest?:  boolean;
}

/** Lap time in ms → formatted "42.331" styled chip. Purple for PB, primary for session best. */
export function LapTimeBadge({ ms, isPersonalBest, isSessionBest }: LapTimeBadgeProps) {
  const seconds = (ms / 1000).toFixed(3);
  const color = isPersonalBest
    ? colors.primary[400]
    : isSessionBest
      ? colors.primary[300]
      : 'inherit';

  return (
    <span
      style={{
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
        fontWeight: 600,
        fontSize: '0.85rem',
        color,
        letterSpacing: '0.02em',
      }}
    >
      {seconds}
    </span>
  );
}

interface DriverRatingBadgeProps {
  rating: number;
  trend?: number;
  size?: 'small' | 'medium' | 'large';
}

/** Circular driver rating badge (e.g. 87) — colour-coded from the mockup. */
export function DriverRatingBadge({ rating, trend, size = 'medium' }: DriverRatingBadgeProps) {
  const sizePx   = size === 'small' ? 32 : size === 'large' ? 52 : 40;
  const fontSize = size === 'small' ? '0.75rem' : size === 'large' ? '1rem' : '0.875rem';

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          width: sizePx,
          height: sizePx,
          borderRadius: '50%',
          border: `2px solid ${colors.primary[500]}`,
          background: `${colors.primary[500]}22`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.primary[400],
          fontWeight: 700,
          fontSize,
          fontFamily: '"Inter", sans-serif',
        }}
      >
        {rating}
      </span>
      {trend !== undefined && (
        <span
          style={{
            fontSize: '0.65rem',
            fontWeight: 600,
            color: trend >= 0 ? colors.success.light : colors.error.light,
            marginTop: 2,
          }}
        >
          {trend >= 0 ? '+' : ''}{trend}
        </span>
      )}
    </span>
  );
}
